<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  watch,
  nextTick,
  onBeforeUpdate,
  onBeforeUnmount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useDateTimeHelper } from '~/composables/useDateTimeHelper';
import { useSmarticoShop } from '~/stores/smartico/shop';

type TTab = 'all' | 'ofertas' | 'points' | 'diamonds' | 'gems';

const props = defineProps<{
  activeTab?: TTab | string;
  expandAllByDefault?: boolean;
}>();

const emit = defineEmits<{
  (e: 'open-item', item: any): void;
}>();

const isLoading = ref(false);
const historyItems = ref<any[]>([]);
const isFetching = ref(false); // Flag para evitar chamadas simultâneas

const API_TIMEOUT_MS = 8000;

async function withTimeout<T>(
  promise: Promise<T>,
  ms = API_TIMEOUT_MS,
): Promise<T> {
  return await Promise.race([
    promise,
    new Promise<T>((_resolve, reject) =>
      setTimeout(
        () => reject(new Error('Timeout ao carregar histórico da loja')),
        ms,
      ),
    ),
  ]);
}

async function fetchHistoryAll() {
  // Previne chamadas simultâneas
  if (isFetching.value) {
    return;
  }

  isFetching.value = true;

  // LIMPA TUDO antes de começar - GARANTE array limpo
  historyItems.value = [];
  isLoading.value = true;

  try {
    const wsapi = (window as any)?._smartico?.api;
    const sapi = wsapi?.api;
    const uid = (window as any)?._smartico_user_id;

    const limitDefault = 100;
    let offset = 0;
    const out: any[] = []; // SEMPRE array NOVO
    let guard = 0;
    let pageCap = 0;

    // Limpa caches uma única vez antes da paginação (apenas wsapi)
    try {
      await wsapi?.clearCaches?.();
    } catch (e) {
      console.warn('⚠️ Falha ao executar clearCaches (ignorado):', e);
    }
    while (guard < 200) {
      let res: any;

      if (sapi && typeof sapi.storeGetPurchasedItems === 'function') {
        res = await withTimeout(
          sapi.storeGetPurchasedItems(uid ?? null, limitDefault, offset),
        );
      } else if (sapi && typeof sapi.storeGetPurchasedItemsT === 'function') {
        const itemsT = await withTimeout(
          sapi.storeGetPurchasedItemsT(uid ?? null, limitDefault, offset),
        );

        const itemsTArray = Array.isArray(itemsT) ? itemsT : [];

        res = {
          items: itemsTArray,
          hasMore: itemsTArray.length === limitDefault,
        };
      } else if (typeof wsapi?.getStorePurchasedItems === 'function') {
        res = await withTimeout(
          wsapi.getStorePurchasedItems(limitDefault, offset),
        );
      } else if (typeof wsapi?.getStorePurchasedItemsT === 'function') {
        const itemsT = await withTimeout(
          wsapi.getStorePurchasedItemsT(limitDefault, offset),
        );

        const itemsTArray = Array.isArray(itemsT) ? itemsT : [];

        res = {
          items: itemsTArray,
          hasMore: itemsTArray.length === limitDefault,
        };
      } else {
        break;
      }

      const items = Array.isArray(res) ? res : (res?.items ?? []);

      if (!pageCap && items.length > 0) pageCap = items.length;
      out.push(...items);

      const hasMore =
        typeof res?.hasMore === 'boolean'
          ? !!res.hasMore
          : items.length === limitDefault;

      if (!hasMore || items.length === 0) break;

      offset += items.length;
      guard++;
    }

    const sortedHistory = out.sort(
      (a: any, b: any) => (b?.purchase_ts ?? 0) - (a?.purchase_ts ?? 0),
    );

    // Dedupe por (store_item_id|id) + purchase_ts + (purchase_id|transaction_id|order_id)
    const _seenKeys = new Set<string>();
    const dedupedHistory = sortedHistory.filter((i: any) => {
      const sid = Number(i?.store_item_id ?? i?.id ?? -1);
      const ts = Number(i?.purchase_ts ?? 0);
      const pid = String(
        (i?.purchase_id ?? i?.transaction_id ?? i?.order_id ?? '') || '',
      );

      const key = `${sid}|${ts}|${pid}`;

      if (_seenKeys.has(key)) return false;
      _seenKeys.add(key);

      return true;
    });

    historyItems.value = dedupedHistory;
  } catch (e) {
    console.error('❌ Erro carregando histórico da loja:', e);
  } finally {
    isLoading.value = false;
    isFetching.value = false;
  }
}

async function refreshHistory() {
  // Força reload completo da store antes de buscar histórico
  const { loadshopItems } = useSmarticoShop();

  try {
    await loadshopItems({ force: true });
  } catch (e) {
    console.warn('⚠️ Erro ao recarregar shopItems antes do histórico:', e);
  }

  return fetchHistoryAll();
}

defineExpose({ refreshHistory });

const { shopItems } = storeToRefs(useSmarticoShop());
const shopItemsMapById = computed(() => {
  const m = new Map<number, any>();
  const items = shopItems.value || [];

  for (const s of items) {
    const id = (s as any)?.id;

    if (typeof id === 'number') m.set(id, s);
  }

  return m;
});

function getFromStore(it: any) {
  const sid = Number(it?.store_item_id || it?.id);

  return Number.isFinite(sid)
    ? (shopItemsMapById.value.get(sid) as any)
    : undefined;
}

const COMBOS_CATEGORY_ID = 488;

function isOfferItem(it: any): boolean {
  const fromStore = getFromStore(it);
  const categories =
    (fromStore as any)?.category_ids ?? (it as any)?.category_ids;

  return Array.isArray(categories) && categories.includes(COMBOS_CATEGORY_ID);
}

function getItemImage(it: any): string {
  const fromStore = getFromStore(it);
  const candidates = [
    it?.image,
    it?.image_url,
    it?.store_item_image,
    it?.ui?.image,
    it?.store_item_ui_definition?.promo_image,
    fromStore?.image,
  ];

  return (
    (candidates.find((x) => typeof x === 'string' && x.length > 0) as
      | string
      | undefined) || ''
  );
}

function getItemName(it: any): string {
  const fromStore = getFromStore(it);
  const candidates = [
    it?.name,
    it?.store_item_name,
    it?.ui?.name,
    it?.store_item_ui_definition?.title,
    fromStore?.name,
    fromStore?.ui?.name,
  ];

  return (
    (candidates.find((x) => typeof x === 'string' && x.length > 0) as
      | string
      | undefined) || 'Item'
  );
}

const { getFormattedDateFromEpoch } = useDateTimeHelper();

function pickNum(obj: any, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = Number((obj as any)?.[k]);

    if (Number.isFinite(v) && v > 0) return v;
  }

  return undefined;
}

function normalizeTab(tabRaw?: string): TTab {
  const t = String(tabRaw ?? 'all')
    .toLowerCase()
    .trim();

  if (['all', 'todos', 'tudo', 'todas'].includes(t)) return 'all';
  if (['ofertas', 'oferta', 'promo', 'promocionais', 'promocional'].includes(t))
    return 'ofertas';
  if (['points', 'coins', 'coin', 'moedas', 'pontos'].includes(t))
    return 'points';
  if (['diamonds', 'diamond', 'diamantes', 'diamante'].includes(t))
    return 'diamonds';
  if (['gems', 'gem', 'gemas', 'gema'].includes(t)) return 'gems';

  return 'all';
}

function getHistoryCurrency(it: any): TTab {
  const pt = String((it as any)?.purchase_type ?? '')
    .toLowerCase()
    .trim();

  if (pt === 'gems') return 'gems';
  if (pt === 'diamonds') return 'diamonds';
  if (pt === 'points' || pt === 'coins' || pt === 'coin') return 'points';

  const fromStore = getFromStore(it);
  const storePt = String((fromStore as any)?.purchase_type ?? '')
    .toLowerCase()
    .trim();

  if (storePt === 'gems') return 'gems';
  if (storePt === 'diamonds') return 'diamonds';
  if (storePt === 'points' || storePt === 'coins' || storePt === 'coin')
    return 'points';

  const diamondsAmt = pickNum(it, [
    'spent_diamonds',
    'amount_diamonds',
    'purchase_diamonds_amount',
    'price_diamonds',
    'diamonds',
  ]);

  if (diamondsAmt) return 'diamonds';

  const gemsAmt = pickNum(it, [
    'spent_gems',
    'amount_gems',
    'purchase_gems_amount',
    'price_gems',
    'gems',
  ]);

  if (gemsAmt) return 'gems';

  return 'points';
}

function getHistoryCurrencyLabel(it: any): string {
  const cur = getHistoryCurrency(it);

  return cur === 'diamonds' ? 'DIAMONDS' : cur === 'gems' ? 'GEMS' : 'COINS';
}

function getCurrencyIconByItem(it: any) {
  const t = getHistoryCurrency(it);

  if (t === 'diamonds') return 'currency-header/diamond';
  if (t === 'gems') return 'currency-header/gem';

  return 'currency-header/coin';
}

function getHistoryAmount(it: any): number | undefined {
  const cur = getHistoryCurrency(it);
  const fromStore = getFromStore(it);

  const specificKeys: Record<TTab, string[]> = {
    gems: [
      'spent_gems',
      'amount_gems',
      'purchase_gems_amount',
      'price_gems',
      'gems',
    ],
    diamonds: [
      'spent_diamonds',
      'amount_diamonds',
      'purchase_diamonds_amount',
      'price_diamonds',
      'diamonds',
    ],
    points: [
      'spent_points',
      'spent_coins',
      'amount_points',
      'price_points',
      'points',
      'coins',
    ],
    all: [],
    ofertas: [],
  };

  const genericKeys = ['purchase_points_amount', 'price'];

  const storeFallbackKeys: Record<TTab, string[]> = {
    gems: ['price_gems'],
    diamonds: ['price_diamonds'],
    points: ['price_points', 'price'],
    all: [],
    ofertas: [],
  };

  let amount = pickNum(it, specificKeys[cur] || []);

  if (amount) return amount;

  amount = pickNum(it, genericKeys);
  if (amount) return amount;

  amount = pickNum(fromStore || {}, storeFallbackKeys[cur] || []);

  return amount;
}

function getHistoryItemQuantity(it: any): number {
  // Tenta diferentes propriedades que podem conter a quantidade
  const quantityKeys = [
    'quantity',
    'amount',
    'count',
    'qty',
    'purchase_quantity',
    'item_quantity',
  ];

  for (const key of quantityKeys) {
    const value = Number(it?.[key]);

    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  // Se não encontrar nenhuma quantidade específica, retorna 1
  return 1;
}

const normalizedActiveTab = computed<TTab>(() =>
  normalizeTab(props.activeTab as string),
);

const filteredShopHistory = computed<any[]>(() => {
  const all = historyItems.value || [];
  const tab = normalizedActiveTab.value;

  if (tab === 'ofertas') {
    return all.filter((it: any) => isOfferItem(it));
  }

  if (tab === 'points' || tab === 'diamonds' || tab === 'gems') {
    return all.filter(
      (it: any) => !isOfferItem(it) && getHistoryCurrency(it) === tab,
    );
  }

  return all;
});

// Função para verificar se dois timestamps são do mesmo dia
function areSameDay(ts1: number, ts2: number): boolean {
  if (!ts1 || !ts2) return false;

  const date1 = new Date(ts1);
  const date2 = new Date(ts2);

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

const groupedShopHistory = computed<any[]>(() => {
  if (!filteredShopHistory.value || filteredShopHistory.value.length === 0) {
    return [];
  }

  const sourceItems = filteredShopHistory.value;

  const groups: {
    id: number;
    ts: number;
    quantity: number;
    totalAmount: number;
    representative: any;
    items: any[];
  }[] = [];

  const WINDOW_GROUP_SECONDS = 5 * 1000; // Reduzido para 5 segundos para ser mais restritivo

  for (const currentItem of sourceItems) {
    const sid = Number(currentItem?.store_item_id || currentItem?.id);
    const ts = Number(currentItem?.purchase_ts) || 0;
    const currentQuantity = getHistoryItemQuantity(currentItem);
    const lastGroup = groups.length > 0 ? groups[groups.length - 1] : null;

    // Verificação mais rigorosa para agrupamento
    const canGroupWithLast =
      lastGroup &&
      lastGroup.id === sid &&
      Math.abs(lastGroup.ts - ts) <= WINDOW_GROUP_SECONDS &&
      areSameDay(lastGroup.ts, ts);

    if (canGroupWithLast) {
      lastGroup.items.push(currentItem);
      lastGroup.quantity += currentQuantity;

      const amount = getHistoryAmount(currentItem) || 0;

      lastGroup.totalAmount += amount;

      if (ts > lastGroup.ts) {
        lastGroup.ts = ts;
        lastGroup.representative = currentItem;
      }
    } else {
      const amount = getHistoryAmount(currentItem) || 0;

      groups.push({
        id: sid,
        ts,
        quantity: currentQuantity,
        totalAmount: amount,
        representative: currentItem,
        items: [currentItem],
      });
    }
  }

  return groups;
});

const historyContainerRef = ref<HTMLElement | null>(null);
const initialItemsToShow = 5;
const itemsToShow = ref<number>(initialItemsToShow);

const visibleShopHistory = computed<any[]>(() =>
  (groupedShopHistory.value || []).slice(0, itemsToShow.value),
);

const totalFilteredHistoryCount = computed<number>(
  () => (groupedShopHistory.value || []).length,
);

const hasMoreItemsToLoad = computed<boolean>(
  () => itemsToShow.value < totalFilteredHistoryCount.value,
);

const canShowLess = computed<boolean>(
  () => itemsToShow.value > initialItemsToShow,
);

function loadMoreItems() {
  itemsToShow.value += initialItemsToShow;
}

function resetItems() {
  itemsToShow.value = initialItemsToShow;
}

function showLessItemsAndScroll() {
  resetItems();
  if (historyContainerRef.value) {
    historyContainerRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}

// ----- Correção de alinhamento v2: Medir conteúdo interno -----
const costValueRefs = ref<(HTMLElement | null)[]>([]);
const quantityValueRefs = ref<(HTMLElement | null)[]>([]);
const maxContentWidth = ref<number>(0);
let resizeObserver: ResizeObserver | null = null;

// Limpa as refs antes de cada atualização para evitar elementos "fantasmas"
onBeforeUpdate(() => {
  costValueRefs.value = [];
  quantityValueRefs.value = [];
});

function setCostValueRef(el: any, idx: number) {
  if (el) costValueRefs.value[idx] = el;
}

function setQuantityValueRef(el: any, idx: number) {
  if (el) quantityValueRefs.value[idx] = el;
}

// Mede a largura do conteúdo de texto para encontrar o máximo
function measureContentWidths() {
  let maxWidth = 0;
  const elements = [...costValueRefs.value, ...quantityValueRefs.value];

  for (const el of elements) {
    if (el && el.offsetParent !== null) {
      // Usamos scrollWidth para pegar a largura real do conteúdo, mesmo que esteja quebrando linha
      const width = el.scrollWidth;

      if (width > maxWidth) {
        maxWidth = width;
      }
    }
  }
  // Define o valor, garantindo um mínimo para não colapsar
  maxContentWidth.value = Math.max(Math.ceil(maxWidth), 40);
}

// Observa a lista de itens visíveis e recalcula as larguras quando ela muda
watch(
  visibleShopHistory,
  async () => {
    // Aguarda o DOM ser atualizado
    await nextTick();
    measureContentWidths();
  },
  { deep: true, immediate: true },
);

// Observa a troca de abas
watch(
  () => props.activeTab,
  () => {
    resetItems();
    maxContentWidth.value = 0;
    nextTick(() => {
      measureContentWidths();
    });
  },
);

onMounted(async () => {
  // O refresh será feito apenas quando explicitamente chamado (openHistory, após compra, etc)

  // Medição inicial robusta
  await nextTick();
  measureContentWidths(); // Garante a medição após o carregamento de fontes customizadas
  if (document.fonts?.ready) {
    document.fonts.ready.then(() => measureContentWidths());
  }

  // Usa ResizeObserver para re-medir de forma eficiente em mudanças de layout
  if (historyContainerRef.value) {
    resizeObserver = new ResizeObserver(() => {
      measureContentWidths();
    });
    resizeObserver.observe(historyContainerRef.value);
  }
});

onBeforeUnmount(() => {
  // Limpa o observer para evitar memory leaks
  if (resizeObserver && historyContainerRef.value) {
    resizeObserver.unobserve(historyContainerRef.value);
  }
});
</script>

<template>
  <div
    v-if="isLoading && (filteredShopHistory?.length ?? 0) === 0"
    class="text-center text-secondary mt-1 mb-1 loading-pulse"
  >
    Carregando histórico...
  </div>

  <div
    v-else-if="filteredShopHistory && filteredShopHistory.length > 0"
    :key="normalizedActiveTab"
    ref="historyContainerRef"
  >
    <div
      v-for="(g, idx) in visibleShopHistory"
      :key="`${g.id}-${g.ts}-${idx}`"
      class="flex flex-col lg:flex-row items-stretch justify-between gap-2 border [background:#010612] backdrop-blur-[10px] p-3 lg:p-5 rounded-[10px] border-solid border-[#1D2329] mb-2.5"
    >
      <!-- BLOCO 1: Ícone + (COMPRA, Nome, CUSTO/QUANTIDADE no mobile) -->
      <div class="flex items-stretch gap-2 flex-1 min-w-0 order-1">
        <NuxtImg
          :alt="getItemName(g.representative)"
          class="w-[72px] lg:w-[70px] lg:h-[70px] object-contain rounded-lg shrink-0 self-stretch"
          :src="getItemImage(g.representative)"
        />
        <div class="flex flex-col gap-1 min-w-0 flex-1">
          <span class="text-white/50 text-[10px] font-semibold leading-4"
            >COMPRA</span
          >
          <span
            class="text-white text-[13px] lg:text-[15px] font-semibold leading-[18px] lg:leading-[19px] truncate"
            :title="getItemName(g.representative)"
          >
            {{ getItemName(g.representative) }}
          </span>

          <!-- CUSTO + QUANTIDADE (MOSTRA SÓ NO MOBILE) -->
          <div class="lg:hidden grid grid-cols-2 gap-2 mt-1">
            <!-- CUSTO -->
            <div class="flex flex-col">
              <span class="text-white/50 text-[10px] font-semibold leading-4"
                >CUSTO</span
              >
              <div class="flex items-center gap-1">
                <NuxtIcon
                  class="text-[16px]"
                  filled
                  :name="getCurrencyIconByItem(g.representative)"
                />
                <span
                  :ref="(el) => setCostValueRef(el, idx)"
                  class="text-[#00C9FF] text-[15px] font-extrabold leading-tight whitespace-nowrap"
                >
                  {{ g.totalAmount?.toLocaleString('pt-BR') }}
                </span>
              </div>
            </div>

            <!-- QUANTIDADE -->
            <div class="flex flex-col">
              <span class="text-white/50 text-[10px] font-semibold leading-4"
                >QUANTIDADE</span
              >
              <div
                :ref="(el) => setQuantityValueRef(el, idx)"
                class="text-white text-base font-semibold leading-tight"
              >
                {{ g.quantity }}
              </div>
            </div>
          </div>
          <!-- /mobile -->
        </div>
      </div>

      <!-- BLOCO 2: CUSTO + QUANTIDADE (MOSTRA SÓ NO DESKTOP) -->
      <div class="order-2 hidden lg:flex items-center justify-center gap-4">
        <!-- CUSTO -->
        <div class="flex flex-col justify-center px-2.5 py-2 rounded-lg gap-1">
          <span class="text-white/50 text-[10px] font-semibold leading-4"
            >CUSTO</span
          >
          <div class="flex items-center gap-2">
            <NuxtIcon
              class="text-[20px]"
              filled
              :name="getCurrencyIconByItem(g.representative)"
            />
            <span
              :ref="(el) => setCostValueRef(el, idx)"
              class="text-[#00C9FF] text-center text-sm font-extrabold leading-5 whitespace-nowrap"
              :style="{
                minWidth: maxContentWidth > 0 ? `${maxContentWidth}px` : 'auto',
              }"
            >
              {{ g.totalAmount?.toLocaleString('pt-BR') }}
              {{ getHistoryCurrencyLabel(g.representative) }}
            </span>
          </div>
        </div>

        <!-- QUANTIDADE -->
        <div class="flex flex-col justify-center px-2.5 py-2 rounded-lg gap-1">
          <div class="text-white/50 text-[10px] font-semibold leading-none"
            >QUANTIDADE</div
          >
          <div
            :ref="(el) => setQuantityValueRef(el, idx)"
            class="text-white text-base font-semibold leading-tight"
            :style="{
              minWidth: maxContentWidth > 0 ? `${maxContentWidth}px` : 'auto',
            }"
          >
            {{ g.quantity }}
          </div>
        </div>
      </div>

      <!-- BLOCO 3: Data + CTA (igual ao seu, continua embaixo no mobile) -->
      <div class="order-3 flex basis-full lg:flex-1 min-w-0 w-full lg:w-auto">
        <div
          class="px-3.5 py-2.5 bg-white/5 rounded-[10px] flex w-full items-center justify-between gap-2 lg:gap-4"
        >
          <div
            class="flex flex-col justify-center items-start gap-[5px] min-w-0"
          >
            <div class="text-white/50 text-[10px] font-semibold leading-none"
              >DATA DE COMPRA</div
            >
            <span
              class="text-white text-[12px] lg:text-[15px] font-semibold leading-[15px]"
            >
              {{ getFormattedDateFromEpoch(g.ts) }}
            </span>
          </div>

          <button
            class="inline-flex h-10 px-2.5 justify-center items-center gap-1.5 rounded-md bg-[radial-gradient(ellipse_138%_168.3%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)] outline outline-1 outline-offset-[-1px] outline-[#FC9984] hover:brightness-90 cursor-pointer transition shrink-0"
            @click="emit('open-item', g.representative)"
          >
            <span
              class="text-white text-center text-xs lg:text-[12px] font-extrabold leading-3 tracking-tight"
            >
              COMPRAR NOVAMENTE
            </span>
            <NuxtIcon
              class="text-[#fdfcfc] text-[18px]"
              :filled="true"
              name="store/cart"
            />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="
        (hasMoreItemsToLoad || canShowLess) &&
        filteredShopHistory.length > 0 &&
        !isLoading
      "
      class="flex justify-center gap-4 mt-3"
    >
      <button
        v-if="canShowLess"
        class="toggleButton"
        @click="showLessItemsAndScroll"
      >
        <span class="text-white text-[13px] font-bold leading-4"
          >Ver Menos</span
        >
      </button>
      <button
        v-if="hasMoreItemsToLoad"
        class="toggleButton"
        @click="loadMoreItems"
      >
        <span class="text-white text-[13px] font-bold leading-4">Ver Mais</span>
      </button>
    </div>

    <div
      v-if="isLoading && (filteredShopHistory?.length ?? 0) > 0"
      class="text-center text-secondary mt-2 loading-pulse"
    >
      Carregando mais...
    </div>
  </div>

  <div
    v-else
    key="history-empty"
    class="alert alert-info"
  >
    <span>Nenhum histórico de compras encontrado.</span>
  </div>
</template>

<style scoped lang="postcss">
.toggleButton {
  @apply flex w-full h-[41px] justify-center items-center gap-[15px] border [background:#010612] backdrop-blur-[10px] p-5 rounded-[10px] border-solid border-[#1D2329] text-white;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.loading-pulse {
  animation: pulse 1.5s infinite ease-in-out;
}
</style>
